import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/ui/modal';
import { useExecuteDeviceFunctionMutation } from '@/redux/api/endpoints/device-functions';
import { AlexaFunctionCard } from './alexa-function-card';
import { AlexaFunctionResult } from './alexa-function-result';
import { buildDeletePvaRequest } from './request-builders';

type DeletePvaFunctionProps = {
  deviceId: number;
  roomEndpoint?: string;
};

export const DeletePvaFunction: FC<DeletePvaFunctionProps> = ({
  deviceId,
  roomEndpoint,
}) => {
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [execute, { data, error, isLoading }] =
    useExecuteDeviceFunctionMutation();

  const confirm = () => {
    if (!roomEndpoint) {
      return;
    }
    setConfirmationOpen(false);
    void execute(buildDeletePvaRequest(deviceId));
  };

  return (
    <>
      <AlexaFunctionCard
        description="Remove persistent visual alerts from this device's Alexa room."
        title="Delete PVA"
      >
        <Button
          disabled={!roomEndpoint || isLoading}
          onClick={() => setConfirmationOpen(true)}
          type="button"
          variant="secondary-warning"
        >
          Delete PVA
        </Button>
        {!roomEndpoint && (
          <p className="mt-2 text-xs text-gray-500">
            This Alexa device has no room endpoint.
          </p>
        )}
        <AlexaFunctionResult data={data} error={error} isLoading={isLoading} />
      </AlexaFunctionCard>
      <Modal
        buttonItems={[
          <Button
            key="cancel"
            onClick={() => setConfirmationOpen(false)}
            variant="secondary"
          >
            Cancel
          </Button>,
          <Button key="confirm" onClick={confirm} variant="primary-warning">
            Delete PVA
          </Button>,
        ]}
        description="This sends a live request to Amazon and removes persistent visual alerts for the selected Alexa room."
        open={confirmationOpen}
        setOpen={setConfirmationOpen}
        title="Confirm PVA deletion"
      >
        <p className="px-6 pb-4 text-sm break-all text-gray-600">
          Room endpoint: {roomEndpoint}
        </p>
      </Modal>
    </>
  );
};
